<template>
  <div class="bg-white brand-input">
    <div v-if="!loadingCampaignBlueprint">
      <!-- Non-empty state -->
      <div>
        <header
          class="sticky top-0 z-40 lg:flex lg:items-center lg:justify-between bg-brand-sand py-2 lg:pt-3 lg:pb-2 border-b border-brand-dark-sand"
        >
          <div v-if="audience" class="flex space-x-2 text-sm font-semibold pl-4 lg:w-96 justify-center lg:justify-start">
            <span class="text-brand-brown whitespace-nowrap">
              <router-link to="/reactivation/campaigns"> Reactivation Campaigns </router-link>
            </span>
            <span class="text-brand-medium-brown"> / </span>
            <span class="text-brand-brown whitespace-nowrap" v-if="abTest">
              <router-link :to="{ name: 'flows', params: { view: 'all' } }">
                {{ abTest.name }}
              </router-link>
              <span class="text-brand-medium-brown"> / </span>
            </span>
            <span class="text-brand-black overflow-ellipsis overflow-hidden whitespace-nowrap">
              {{ audience.name || 'New Campaign' }}
            </span>
          </div>
          <div v-else>
            <router-link
              class="text-center lg:text-left lg:flex-grow-0 justify-left lg:w-96 flex-grow-1 flex items-center pl-4 cursor-pointer"
              :to="{ path: segmentsEnabled ? '/reactivation' : '/' }"
            >
              <span><font-awesome-icon :icon="['fas', 'arrow-left']" class="text-brand-brown"></font-awesome-icon></span>
              <img src="@/assets/brand/icon.svg" class="w-auto inline-block h-14 p-2.5 rounded-full" alt="Logo" />
            </router-link>
          </div>
          <div
            class="mx-auto flex flex-grow max-w-screen-sm text-center text-brand-brown font-semibold text-sm space-x-4 lg:space-x-8 justify-center mt-4 lg:mt-0"
          >
            <div v-for="(step, i) in steps" :key="step.value">
              <builder-tab :value="step.value" :label="step.name" :active="i === currentStepIndex" @select="(v) => handleStepClick(i, v)" />
            </div>
          </div>
          <!-- <steps :steps="buildSteps" :current="1" @step-click="handleStepClick" class="flex-grow" /> -->
          <div
            class="flex flex-col items-center flex-grow-0 mt-2 mx-auto lg:mx-3 lg:mt-0 lg:flex-shrink-0 lg:space-x-3 lg:flex-row lg:w-96 lg:mr-4 lg:justify-end"
          >
            <!-- Campaign blueprint save state -->
            <save-state-tag :status="saveState" />
            <div class="mx-auto my-2 lg:inline-block lg:my-0 space-x-3">
              <!-- Publish campaign blueprint button -->
              <div
                v-if="!hasUnpublishedChanges && saveState === SAVE_STATE.SAVED"
                class="bg-brand-medium-blue text-brand-blue inline-flex items-center px-4 py-2 text-sm leading-6 font-medium rounded-md transition ease-in-out duration-150 space-x-2"
              >
                <span class="cursor-default">Changes Pushed</span>
              </div>
              <el-button
                v-else
                v-can-enable="'reactivation:write'"
                type="primary"
                @click="handlePublish"
                :loading="publishLoading"
                :disabled="saveState !== SAVE_STATE.SAVED"
                id="flow-publish"
                ><span class="font-bold">{{ publishLabel }}</span></el-button
              >
            </div>
          </div>
        </header>
        <main v-if="campaignBlueprint" v-loading="publishLoading || loadState === LOAD_STATE.LOADING">
          <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <section>
              <div class="container max-w-screen-xl mt-8 rounded-lg">
                <CampaignEditor v-if="steps[currentStepIndex].value === 'editor'" @next="nextStep" />
                <AudienceEditor v-if="steps[currentStepIndex].value === 'audience'" @next="nextStep" />
              </div>
            </section>
            <!-- UPGRADE MODAL -->
            <el-dialog :visible.sync="showUpgradePrompt" custom-class="mx-auto max-w-xl rounded-lg" :close-on-click-modal="true">
              <div class="has-border-radius-16">
                <div class="max-w-md mx-auto my-2 text-brand-brown font-semibold">
                  <h3 class="text-xl leading-6 my-6 font-bold text-gray-900">Ready to go live with Reactivations?</h3>
                  <p class="break-normal text-md font-semibold">
                    Great news. Now that you're ready to start winning back customers with Reactivations, it's time for Churnkey to gather your
                    payment details. Once that's completed, you'll start your 14-day trial.
                  </p>
                </div>
                <div slot="footer" class="dialog-footer mt-8 border-t-2 border-brand-sand bg-brand-light-sand">
                  <div class="max-w-md mx-auto py-5 flex justify-center space-x-4 items-center">
                    <el-button @click="showUpgradePrompt = false" id="publish-payment-cancel">Not Now</el-button>
                    <a href="/upgrade">
                      <el-button type="primary">View Our Plans</el-button>
                    </a>
                  </div>
                </div>
              </div>
            </el-dialog>
          </div>
        </main>
        <main v-else></main>
      </div>
    </div>
    <div v-else>
      <!-- Empty state -->
      <div class="bg-brand-sand w-full h-20"></div>
      <div class="container max-w-screen-md pt-20 space-y-12" element-loading-background="rgba(255, 255, 255, 0.05)">
        <content-loader primaryColor="#f8efe6" secondaryColor="#f8efe655" class="w-full" :speed="1.5" :primaryOpacity="0.9" :secondaryOpacity="0.9" />
        <content-loader
          primaryColor="#f8efe6"
          secondaryColor="#f8efe655"
          class="w-full"
          :speed="1.5"
          :primaryOpacity="0.65"
          :secondaryOpacity="0.65"
        />
        <content-loader primaryColor="#f8efe6" secondaryColor="#f8efe655" class="w-full" :speed="2.5" :primaryOpacity="0.5" :secondaryOpacity="0.5" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as mutations from '@/store/mutation-types';
import * as actions from '@/store/action-types';
import { SAVE_STATE, LOAD_STATE } from '@/store/shared';
import CampaignEditor from '@/components/Reactivation/CampaignEditor.vue';
import AudienceEditor from '@/components/Reactivation/AudienceEditor.vue';
import BuilderTab from '@/components/UI/BuilderTab.vue';
import SaveStateTag from '@/components/UI/SaveStateTag.vue';
import { ContentLoader } from 'vue-content-loader';
import eventBus from '@/helpers/event-bus';

const baseBuildSteps = [{ name: 'Reactivation Campaign', value: 'editor' }];

const segmentedBuildSteps = [
  { name: 'Audience', value: 'audience' },
  { name: 'Reactivation Campaign', value: 'editor' },
];

export default {
  name: 'ReactivationCampaignBuilder',
  components: {
    // Steps,
    BuilderTab,
    SaveStateTag,
    ContentLoader,
    CampaignEditor,
    AudienceEditor,
  },
  data() {
    return {
      SAVE_STATE,
      LOAD_STATE,
      publishLoading: false,
      currentStepIndex: this.$route.query.step || 0,
      loadingPortal: false,
      showUpgradePrompt: false,
      loadingCampaignBlueprint: true,
    };
  },
  async created() {
    await this.$store.dispatch(actions.SYNC_REACTIVATION);
    this.$store.commit(mutations.SET_CURRENT_REACTIVATION_CAMPAIGN_BLUEPRINT_ID, this.$route.params.id);
    this.loadingCampaignBlueprint = false;
  },
  computed: {
    ...mapGetters({
      campaignBlueprint: 'currentReactivationCampaignBlueprint',
      saveState: 'currentReactivationCampaignBlueprintSaveState',
      loadState: 'currentReactivationCampaignBlueprintLoadState',
    }),
    ...mapGetters(['segmentsEnabled', 'isLocal', 'isAdmin', 'payingCustomer']),
    audience() {
      return this.campaignBlueprint?.audience;
    },
    steps() {
      if (this.audience) {
        return segmentedBuildSteps;
      }
      return baseBuildSteps;
    },
    abTest() {
      return this.campaignBlueprint?.abTests?.find((abTest) => !abTest.completedAt);
    },
    published() {
      return !!this.campaignBlueprint?.publishedAt;
    },
    hasUnpublishedChanges() {
      if (!this.campaignBlueprint) return false;
      const { publishedAt, updatedAt } = this.campaignBlueprint;
      return !publishedAt || new Date(updatedAt) - new Date(publishedAt) > 0;
    },
    publishLabel() {
      if (this.published) {
        return 'Push Changes';
      }
      if (this.audience) {
        return 'Activate Segment';
      }
      return 'Activate Campaign';
    },
  },
  methods: {
    openChat() {
      eventBus.$emit('openChat');
    },
    handleStepClick(stepIndex) {
      this.currentStepIndex = stepIndex;
    },
    nextStep() {
      this.handleStepClick(this.currentStepIndex + 1);
    },
    backStep() {
      this.handleStepClick(this.currentStepIndex - 1);
    },
    handleSave() {
      if (this.campaignBlueprint) {
        this.$store.dispatch(actions.SCHEDULE_SAVE_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: this.campaignBlueprint._id, delay: 0 });
      }
    },
    async handlePublish() {
      if (!this.isLocal && !this.isAdmin && !this.payingCustomer) {
        this.showUpgradePrompt = true;
        return;
      }
      this.publishLoading = true;

      // if it hasn't been published before, we also want to enable it before publishing
      if (!this.published) {
        this.$store.commit(mutations.STORE_REACTIVATION_CAMPAIGN_BLUEPRINT_DATA, { id: this.campaignBlueprint._id, data: { enabled: true } });
        // no need to await, this will get pushed on publish
        this.$store.dispatch(actions.SCHEDULE_SAVE_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: this.campaignBlueprint._id, delay: 0 });
      }

      const { resultsByType } = await this.$store.dispatch(actions.PUBLISH_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: this.campaignBlueprint._id });
      if (resultsByType) {
        const aggregateResults = Object.keys(resultsByType).reduce(
          (acc, curr) => {
            acc.nInserted += resultsByType[curr].nInserted;
            acc.nUpserted += resultsByType[curr].nUpserted;
            acc.nMatched += resultsByType[curr].nMatched;
            acc.nModified += resultsByType[curr].nModified;
            acc.nRemoved += resultsByType[curr].nRemoved;
            return acc;
          },
          { nInserted: 0, nUpserted: 0, nMatched: 0, nModified: 0, nRemoved: 0 }
        );
        const messages = [];
        if (aggregateResults.nInserted > 0) {
          messages.push(`${aggregateResults.nInserted} new emails scheduled`);
        }
        if (aggregateResults.nModified > 0) {
          messages.push(`${aggregateResults.nModified} emails updated`);
        }
        if (aggregateResults.nRemoved > 0) {
          messages.push(`${aggregateResults.nRemoved} scheduled emails removed`);
        }
        if (messages.length) {
          this.$notify({
            title: 'Campaign Published',
            message: `<ul class="list-disc space-y-1">${messages.map((d) => `<li>${d}</li>`).join('')}</ul>`,
            type: 'success',
            duration: 5000,
            dangerouslyUseHTMLString: true,
          });
        } else {
          this.$notify({
            title: 'Campaign Published',
            message: `Emails changes applied successfully`,
            type: 'success',
            duration: 5000,
          });
        }
      }
      this.publishLoading = false;
    },
  },
};
</script>
<style lang="scss">
.el-dialog {
  border-radius: 10px;
  .el-dialog__header {
    padding-top: 0;
  }
  .el-dialog__body {
    padding: 0;
  }
  .dialog-footer {
    border-radius: 0 0 10px 10px;
  }
  .el-dialog__headerbtn {
    display: none;
  }
  /* Fix Bouncing Loader Button */
  .el-button--primary:focus {
    outline: none;
  }
  .el-loading-mask:focus {
    outline: none;
  }
  .el-loading-spinner:focus {
    outline: none;
  }
  .el-loading-spinner .circular:focus {
    outline: none;
  }
}
</style>

<style lang="scss" scoped>
.pulse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #6d6156;
  cursor: pointer;
  box-shadow: 0 0 0 #6d615677;
  animation: pulse 1.5s infinite;
}

@-webkit-keyframes pulse {
  0% {
    -webkit-box-shadow: 0 0 0 0 #6d615677;
  }
  70% {
    -webkit-box-shadow: 0 0 0 10px rgba(204, 169, 44, 0);
  }
  100% {
    -webkit-box-shadow: 0 0 0 0 rgba(204, 169, 44, 0);
  }
}
@keyframes pulse {
  0% {
    -moz-box-shadow: 0 0 0 0 #6d615677;
    box-shadow: 0 0 0 0 #6d615677;
  }
  70% {
    -moz-box-shadow: 0 0 0 10px rgba(204, 169, 44, 0);
    box-shadow: 0 0 0 10px rgba(204, 169, 44, 0);
  }
  100% {
    -moz-box-shadow: 0 0 0 0 rgba(204, 169, 44, 0);
    box-shadow: 0 0 0 0 rgba(204, 169, 44, 0);
  }
}
</style>
